import { api, getToken } from "./api";
import { t, getLang, pluralDays } from "./i18n";

export async function renderSubscription(el: HTMLElement): Promise<void> {
  if (!getToken()) {
    el.innerHTML = `<div class="card empty">${t("billing.first_login")} <a href="#/login">${t("nav.login")}</a></div>`;
    return;
  }
  el.innerHTML = `<div class="card"><p class="hint">…</p></div>`;
  let data;
  try { data = await api.subscription(); }
  catch (e: any) { el.innerHTML = `<div class="card empty">${t("login.error", { msg: e.message })}</div>`; return; }

  const cur = data.current;
  const status = cur.active && cur.expires_at
    ? `<div class="sub-status active">
         <span class="dot-on"></span>
         <span>${t("sub.active")} <b>${escape(cur.tier_name || cur.tier_id || "")}</b></span>
         <span class="sub-ttl">${t("sub.active_until", { date: formatDate(cur.expires_at) })}</span>
       </div>`
    : `<div class="sub-status inactive">
         <span class="dot-off"></span><span>${t("sub.inactive")}</span>
       </div>`;

  const sortedTiers = [...data.tiers].sort((a, b) => a.price_ton - b.price_ton);
  const best = pickBestValue(sortedTiers);

  el.innerHTML = `
    <h2>${t("sub.title")}</h2>
    ${status}
    <div class="tier-grid">
      ${sortedTiers.map((tier, i) => `
        <div class="tier-card ${tier.id === best ? 'tier-best' : ''} ${cur.tier_id === tier.id ? 'tier-current' : ''}">
          ${tier.id === best ? `<div class="tier-badge">${t("sub.best_value")}</div>` : ""}
          <div class="tier-name">${escape(tier.name)}</div>
          <div class="tier-price">${tier.price_ton} <span>TON</span></div>
          <div class="tier-sub">${tierPitch(i, sortedTiers.length)}</div>
          <ul class="tier-feats">
            ${featuresFor(i, sortedTiers.length, tier.days).map(f => `<li>${f}</li>`).join("")}
          </ul>
          <div class="tier-actions">
            <a class="btn ton-btn" href="${tonLink(data.ton_wallet, tier.price_ton, tier.memo)}">
              <svg viewBox="0 0 32 32" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M16 32C24.837 32 32 24.837 32 16S24.837 0 16 0 0 7.163 0 16s7.163 16 16 16zM11.013 8.93h9.974c1.83 0 2.995 1.974 2.075 3.566l-6.155 10.659a1.046 1.046 0 0 1-1.811 0L8.94 12.496c-.919-1.59.244-3.565 2.073-3.565zm.42 1.46c-.55 0-.898.594-.621 1.07l4.604 7.97V10.39h-3.983zm9.137 0h-3.983v9.04l4.604-7.97c.276-.476-.072-1.07-.621-1.07z"/></svg>
              ${t("pay.open_wallet")}
            </a>
            <button class="btn ghost" data-tier="${tier.id}">${t("sub.show_details")}</button>
          </div>
        </div>
      `).join("")}
    </div>
    <p class="hint sub-instructions">${t("pay.instructions")}</p>
    <div id="pay-modal" class="modal" hidden>
      <div class="modal-backdrop" data-close></div>
      <div class="modal-card">
        <button class="modal-x" data-close aria-label="×">×</button>
        <h2 id="pm-title"></h2>
        <p class="hint" id="pm-sub"></p>
        <div class="pay-step">
          <div class="pay-label">${t("pay.wallet")}</div>
          <div class="pay-val">
            <code id="pm-wallet"></code>
            <button class="btn ghost copy" data-copy="pm-wallet">${t("pay.copy")}</button>
          </div>
        </div>
        <div class="pay-step">
          <div class="pay-label">${t("pay.memo")}</div>
          <div class="pay-val">
            <code id="pm-memo"></code>
            <button class="btn ghost copy" data-copy="pm-memo">${t("pay.copy")}</button>
          </div>
        </div>
        <div class="pay-step">
          <div class="pay-label">${t("pay.amount")}</div>
          <div class="pay-val"><code id="pm-amount"></code></div>
        </div>
        <a class="btn ton-btn" id="pm-ton" href="#">
          <svg viewBox="0 0 32 32" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M16 32C24.837 32 32 24.837 32 16S24.837 0 16 0 0 7.163 0 16s7.163 16 16 16zM11.013 8.93h9.974c1.83 0 2.995 1.974 2.075 3.566l-6.155 10.659a1.046 1.046 0 0 1-1.811 0L8.94 12.496c-.919-1.59.244-3.565 2.073-3.565zm.42 1.46c-.55 0-.898.594-.621 1.07l4.604 7.97V10.39h-3.983zm9.137 0h-3.983v9.04l4.604-7.97c.276-.476-.072-1.07-.621-1.07z"/></svg>
          ${t("pay.open_wallet")}
        </a>
        <p class="hint" id="pm-status">${t("pay.waiting")}</p>
      </div>
    </div>
  `;

  const modal = el.querySelector<HTMLElement>("#pay-modal")!;
  let pollAbort: { cancelled: boolean } | null = null;

  function open(tierId: string) {
    const tier = sortedTiers.find(x => x.id === tierId);
    if (!tier) return;
    el.querySelector("#pm-title")!.textContent = t("pay.title", { name: tier.name });
    el.querySelector("#pm-sub")!.textContent = t("pay.subtitle", { days: tier.days, plural: pluralDays(tier.days) });
    el.querySelector("#pm-wallet")!.textContent = data!.ton_wallet;
    el.querySelector("#pm-memo")!.textContent = tier.memo;
    el.querySelector("#pm-amount")!.textContent = `${tier.price_ton} TON`;
    el.querySelector<HTMLAnchorElement>("#pm-ton")!.href = tonLink(data!.ton_wallet, tier.price_ton, tier.memo);
    el.querySelector("#pm-status")!.textContent = t("pay.waiting");
    modal.hidden = false;

    pollAbort = { cancelled: false };
    pollActivation(tierId, pollAbort);
  }

  function close() {
    modal.hidden = true;
    if (pollAbort) pollAbort.cancelled = true;
    pollAbort = null;
  }

  el.querySelectorAll<HTMLButtonElement>("[data-tier]").forEach(b => {
    b.addEventListener("click", () => open(b.dataset.tier!));
  });
  el.querySelectorAll<HTMLElement>("[data-close]").forEach(b => {
    b.addEventListener("click", close);
  });
  el.querySelectorAll<HTMLButtonElement>("[data-copy]").forEach(b => {
    b.addEventListener("click", async () => {
      const id = b.dataset.copy!;
      const txt = el.querySelector<HTMLElement>(`#${id}`)?.textContent || "";
      try {
        await navigator.clipboard.writeText(txt);
        const orig = b.textContent;
        b.textContent = t("pay.copied");
        setTimeout(() => { b.textContent = orig; }, 1500);
      } catch {
        b.textContent = t("pay.copy_failed");
      }
    });
  });

  async function pollActivation(tierId: string, ctl: { cancelled: boolean }) {
    const status = el.querySelector<HTMLElement>("#pm-status")!;
    const start = Date.now();
    while (!ctl.cancelled && Date.now() - start < 15 * 60_000) {
      await new Promise(r => setTimeout(r, 4000));
      if (ctl.cancelled) return;
      try {
        const fresh = await api.subscription();
        if (fresh.current.active && fresh.current.tier_id === tierId) {
          status.textContent = t("pay.success");
          await new Promise(r => setTimeout(r, 1500));
          if (!ctl.cancelled) renderSubscription(el);
          return;
        }
      } catch { /* retry */ }
    }
    if (!ctl.cancelled) status.textContent = t("pay.timeout");
  }
}

export function tonLink(wallet: string, amountTon: number, memo: string): string {
  const nano = Math.round(amountTon * 1e9);
  const params = new URLSearchParams({
    amount: String(nano),
    text: memo,
  });
  return `ton://transfer/${wallet}?${params}`;
}

function tierPitch(idx: number, total: number): string {
  if (total === 1) return t("sub.tier.single");
  if (idx === 0) return t("sub.tier.try");
  if (idx === total - 1) return t("sub.tier.pro");
  return t("sub.tier.optimal");
}

function featuresFor(idx: number, total: number, days: number): string[] {
  const base = [
    t("sub.feat.access_days", { days, plural: pluralDays(days) }),
    t("sub.feat.all_tools"),
    t("sub.feat.no_limits"),
  ];
  if (total > 1 && idx === total - 1) base.push(t("sub.feat.priority"));
  if (idx > 0) base.push(t("sub.feat.support"));
  return base;
}

function pickBestValue(tiers: { id: string; price_ton: number; credits: number; days: number }[]): string {
  if (!tiers.length) return "";
  let bestId = tiers[0].id;
  let bestRatio = tiers[0].credits / Math.max(tiers[0].price_ton, 0.01);
  for (const tier of tiers.slice(1)) {
    const r = tier.credits / Math.max(tier.price_ton, 0.01);
    if (r > bestRatio) { bestRatio = r; bestId = tier.id; }
  }
  return bestId;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    const locale = getLang() === "ru" ? "ru-RU" : "en-US";
    return d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso.slice(0, 10); }
}

function escape(s: string): string {
  return String(s ?? "").replace(/[&<>"']/g, c => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!
  ));
}
